/**
 * specialty-other.js — clinic-edit 專長項目「其他（請說明）」條件式揭露
 *
 * 對照 HTML（src/pages/admin/clinic-edit.html · CARD5）：
 *   <div class="check-grid__full">
 *     <label class="form-check check-grid__other-toggle">
 *       <input class="form-check__input" type="checkbox" name="specialty-other-toggle"
 *              aria-controls="specialty-other-detail" aria-expanded="false">
 *       <span class="form-check__box"></span>其他（請說明）
 *     </label>
 *     <div class="form-field check-grid__other-detail" id="specialty-other-detail" hidden>
 *       <label class="visually-hidden" for="specialty-other-input">其他療程名稱</label>
 *       <input class="form-field__input" id="specialty-other-input" name="specialty-other" … disabled>
 *       <p class="form-field__hint" id="specialty-other-hint">…</p>
 *     </div>
 *   </div>
 *
 * 行為：
 *   勾「其他」 → 解 hidden（display:flex）→ 啟用 input → aria-expanded=true
 *              → 次幀加 .is-open 觸發 fade/上滑進場 → focus 落 input。
 *   取消勾    → 移除 .is-open 播退場 → 退場結束（reduced-motion 立即）→ 設 hidden
 *              + disable input（不送出 / 不可 Tab）+ aria-expanded=false + 清空輸入值。
 *
 * 與 admin-form-edit.js 共存：input 收合時 disabled，validateField() 對 disabled 早退（不誤驗）；
 * 揭露時為一般選填 text input（無 required），驗證框架不會擋空。互不綁同一事件、不互蓋。
 *
 * 揭露區 .form-field 是 display:flex，靠 [hidden] 切顯隱 → CSS 已補
 * .check-grid__other-detail[hidden]{display:none} 守衛（admin-forms2.css）。
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init；無對應 DOM（找不到 toggle / detail）即 no-op，
 *   可安全在所有 admin/auth 頁載入。
 */

// prefersReducedMotion 收進共用 util（原 inline 同式收斂，import 為 ESM hoisted）
import { prefersReducedMotion } from '../utils/dom.js';

function expand(toggle, detail, input) {
  detail.hidden = false;
  if (input) input.disabled = false;
  toggle.setAttribute('aria-expanded', 'true');

  if (prefersReducedMotion()) {
    detail.classList.add('is-open');
    if (input) input.focus();
    return;
  }
  // 次幀加 .is-open：先讓 display:none→flex 落地，transition 才會從收合態起跑（否則無過渡）
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      detail.classList.add('is-open');
    });
  });
  // focus 不等動畫（即時可輸入；preventScroll 避免與進場互搶捲動）
  if (input) input.focus({ preventScroll: true });
}

function collapse(toggle, detail, input) {
  toggle.setAttribute('aria-expanded', 'false');

  const finalize = () => {
    detail.hidden = true;
    if (input) {
      input.disabled = true;
      input.value = ''; // 收合即捨棄自填值（取消「其他」= 不送出自填）
    }
  };

  if (prefersReducedMotion()) {
    detail.classList.remove('is-open');
    finalize();
    return;
  }

  // 播退場（移 .is-open → fade/上滑），退場結束才 hidden + disable，避免中途視覺跳掉。
  // transitionend 一次性監聽 opacity；加安全 timeout 兜底（被打斷 / 不觸發時仍收尾）。
  let done = false;
  const onEnd = (e) => {
    if (e && e.target !== detail) return; // 只認 detail 自身（不接子元素冒泡）
    if (e && e.propertyName && e.propertyName !== 'opacity') return;
    if (done) return;
    done = true;
    detail.removeEventListener('transitionend', onEnd);
    window.clearTimeout(safety);
    finalize();
  };
  detail.addEventListener('transitionend', onEnd);
  // 安全兜底：略長於 --motion-base(260ms)
  const safety = window.setTimeout(() => onEnd(), 360);

  detail.classList.remove('is-open');
}

function bind(toggle) {
  const detail = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!detail) return; // no-op：揭露區缺失
  const input = detail.querySelector('.form-field__input');

  // 對齊初始 DOM 狀態（demo 預設未勾 → 收合 + disabled，HTML 已寫死；此處保險同步一次）
  if (toggle.checked) {
    expand(toggle, detail, input);
  } else {
    detail.hidden = true;
    detail.classList.remove('is-open');
    if (input) input.disabled = true;
    toggle.setAttribute('aria-expanded', 'false');
  }

  toggle.addEventListener('change', () => {
    if (toggle.checked) expand(toggle, detail, input);
    else collapse(toggle, detail, input);
  });
}

function init(root = document) {
  const toggle = root.querySelector('input[name="specialty-other-toggle"][aria-controls]');
  if (!toggle) return; // no-op：非 clinic-edit 專長項目頁
  bind(toggle);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

export { init };
